import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Loader2, Upload } from "lucide-react";
import ImageUpload from "@/components/ImageUpload";
import PinPicker from "@/components/PinPicker";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

const TYPES = ["HMO", "House", "Flat", "Studio", "Garage"];
const STATUSES = ["Available", "Pending Lease", "Rented"];

const EMPTY = {
  title: "", type: "HMO", location: "", lat: "", lng: "", bedrooms: 1, bathrooms: 1,
  status: "Available", image: "", galleryText: "", floorplans: [], description: "", amenitiesText: "",
  rooms: [],
};

export default function PropertyFormModal({ open, onOpenChange, property, onSaved }) {
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [galleryUploading, setGalleryUploading] = useState(false);
  const [floorplansUploading, setFloorplansUploading] = useState(false);
  const [roomUploading, setRoomUploading] = useState({});
  const galleryInputRef = useRef(null);
  const floorplanInputRef = useRef(null);
  const roomFileRefs = useRef({});

  useEffect(() => {
    if (!open) return;
    if (property) {
      setForm({
        title: property.title || "",
        type: property.type || "HMO",
        location: property.location || "",
        lat: property.lat ?? "",
        lng: property.lng ?? "",
        bedrooms: property.bedrooms ?? 1,
        bathrooms: property.bathrooms ?? 1,
        status: property.status || "Available",
        image: property.image || "",
        galleryText: (property.gallery || []).join("\n"),
        floorplans: [property.floorplan, ...(property.floorplans || [])].filter(Boolean),
        description: property.description || "",
        amenitiesText: (property.amenities || []).join(", "),
        rooms: (property.rooms || []).map((r) => ({ ...r })),
      });
    } else {
      setForm(EMPTY);
      api
        .get("/properties")
        .then((r) => {
          const tpl =
            r.data.find((p) => /navigation/i.test(p.title)) || r.data.find((p) => p.type === "HMO");
          if (tpl) {
            setForm((f) => ({
              ...f,
              description: f.description || tpl.description || "",
              amenitiesText: f.amenitiesText || (tpl.amenities || []).join(", "),
            }));
          }
        })
        .catch(() => {});
    }
  }, [open, property]);

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target ? e.target.value : e }));
  const setValue = (key, value) => setForm((f) => ({ ...f, [key]: value }));

  const setRoom = (i, key, value) =>
    setForm((f) => ({ ...f, rooms: f.rooms.map((r, idx) => (idx === i ? { ...r, [key]: value } : r)) }));
  const addRoom = () =>
    setForm((f) => ({
      ...f,
      rooms: [...f.rooms, { title: `Room ${f.rooms.length + 1}`, rent: "", status: "Available", ensuite: false, size_m2: "", description: "", image: "", gallery: [] }],
    }));
  const removeRoom = (i) =>
    setForm((f) => ({ ...f, rooms: f.rooms.filter((_, idx) => idx !== i) }));

  const uploadGalleryPhotos = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = "";
    if (!files.length) return;
    setGalleryUploading(true);
    const urls = [];
    for (const file of files) {
      try {
        const fd = new FormData();
        fd.append("file", file);
        const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        urls.push(data.url);
      } catch (err) {
        toast.error(`Failed to upload ${file.name}`);
      }
    }
    if (urls.length) {
      setForm((f) => ({ ...f, galleryText: [f.galleryText.trim(), ...urls].filter(Boolean).join("\n") }));
      toast.success(`${urls.length} photo${urls.length > 1 ? "s" : ""} added to gallery`);
    }
    setGalleryUploading(false);
  };

  const uploadRoomPhotos = async (i, e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = "";
    if (!files.length) return;
    setRoomUploading((s) => ({ ...s, [i]: true }));
    const urls = [];
    for (const file of files) {
      try {
        const fd = new FormData();
        fd.append("file", file);
        const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        urls.push(data.url);
      } catch (err) {
        toast.error(`Failed to upload ${file.name}`);
      }
    }
    if (urls.length) {
      setForm((f) => ({
        ...f,
        rooms: f.rooms.map((r, idx) => (idx === i ? { ...r, gallery: [...(r.gallery || []), ...urls] } : r)),
      }));
      toast.success(`${urls.length} photo${urls.length > 1 ? "s" : ""} added to ${form.rooms[i]?.title || "room"}`);
    }
    setRoomUploading((s) => ({ ...s, [i]: false }));
  };

  const uploadFloorplans = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = "";
    if (!files.length) return;
    setFloorplansUploading(true);
    const urls = [];
    for (const file of files) {
      try {
        const fd = new FormData();
        fd.append("file", file);
        const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        urls.push(data.url);
      } catch (err) {
        toast.error(`Failed to upload ${file.name}`);
      }
    }
    if (urls.length) {
      setForm((f) => ({ ...f, floorplans: [...f.floorplans, ...urls] }));
      toast.success(`${urls.length} floor plan${urls.length > 1 ? "s" : ""} added`);
    }
    setFloorplansUploading(false);
  };

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    const payload = {
      title: form.title.trim(),
      type: form.type,
      location: form.location.trim(),
      lat: form.lat === "" ? null : Number(form.lat),
      lng: form.lng === "" ? null : Number(form.lng),
      bedrooms: Number(form.bedrooms) || 0,
      bathrooms: Number(form.bathrooms) || 0,
      status: form.status,
      featured: property?.featured || false,
      order: property?.order ?? null,
      image: form.image.trim(),
      gallery: form.galleryText.split(/[\n,]+/).map((s) => s.trim()).filter(Boolean),
      floorplan: "",
      floorplans: form.floorplans.map((u) => (u || "").trim()).filter(Boolean),
      description: form.description.trim(),
      amenities: form.amenitiesText.split(",").map((s) => s.trim()).filter(Boolean),
      rooms: form.rooms
        .filter((r) => r.title.trim())
        .map((r) => ({
          ...r,
          title: r.title.trim(),
          rent: Number(r.rent) || 0,
          size_m2: r.size_m2 === "" || r.size_m2 == null ? null : Number(r.size_m2),
          image: r.image?.trim() || "",
          gallery: (r.gallery || []).map((u) => (u || "").trim()).filter(Boolean),
          description: r.description?.trim() || "",
        })),
    };
    try {
      if (property) {
        await api.put(`/properties/${property.id}`, payload);
        toast.success(`"${payload.title}" updated`);
      } else {
        await api.post("/properties", payload);
        toast.success(`"${payload.title}" published`);
      }
      onOpenChange(false);
      onSaved();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto bg-white dark:bg-[#1A1715]" data-testid="admin-property-modal">
        <DialogHeader>
          <DialogTitle className="font-serif text-2xl">
            {property ? "Edit Property" : "Add New Property"}
          </DialogTitle>
        </DialogHeader>
        <form onSubmit={save} className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-2">
          <div className="sm:col-span-2 space-y-1.5">
            <Label>Property title</Label>
            <Input data-testid="admin-modal-title-input" required value={form.title} onChange={set("title")} placeholder="e.g. 24 Elm Grove — 6 Room HMO" />
          </div>
          <div className="space-y-1.5">
            <Label>Type</Label>
            <Select value={form.type} onValueChange={set("type")}>
              <SelectTrigger data-testid="admin-modal-type-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                {TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          {form.rooms.length === 0 && (
            <div className="space-y-1.5">
              <Label>Availability</Label>
              <Select value={form.status} onValueChange={set("status")}>
                <SelectTrigger data-testid="admin-modal-status-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          )}
          <div className="space-y-1.5">
            <Label>Location</Label>
            <Input data-testid="admin-modal-location-input" required value={form.location} onChange={set("location")} placeholder="Neighbourhood, City + postcode" />
          </div>
          <div className="sm:col-span-2 space-y-1.5">
            <Label>Map pin — set the exact spot of the house</Label>
            <PinPicker
              testId="admin-modal-pin-map"
              lat={form.lat === "" ? null : Number(form.lat)}
              lng={form.lng === "" ? null : Number(form.lng)}
              locationHint={form.location}
              onPick={(la, ln) => setForm((f) => ({ ...f, lat: la, lng: ln }))}
            />
            {form.lat !== "" && form.lng !== "" && (
              <div className="flex items-center gap-3">
                <p data-testid="admin-modal-pin-coords" className="text-xs font-mono text-[#2D4B3E] dark:text-[#4E826B]">
                  Pin set at {Number(form.lat).toFixed(5)}, {Number(form.lng).toFixed(5)}
                </p>
                <button
                  type="button"
                  data-testid="admin-modal-pin-clear"
                  onClick={() => setForm((f) => ({ ...f, lat: "", lng: "" }))}
                  className="text-xs text-[#A05A32] dark:text-[#D9773F] hover:underline"
                >
                  Clear pin
                </button>
              </div>
            )}
          </div>
          <div className="space-y-1.5">
            <Label>Bedrooms</Label>
            <Input data-testid="admin-modal-beds-input" required type="number" min="0" step="0.5" value={form.bedrooms} onChange={set("bedrooms")} />
          </div>
          <div className="sm:col-span-2 space-y-1.5">
            <Label>Bathrooms</Label>
            <Input data-testid="admin-modal-baths-input" required type="number" min="0" step="0.5" value={form.bathrooms} onChange={set("bathrooms")} />
          </div>
          <div className="sm:col-span-2 space-y-1.5">
            <Label>Primary photo</Label>
            <ImageUpload
              testId="admin-modal-image-input"
              value={form.image}
              onChange={(v) => setValue("image", v)}
            />
          </div>
          <div className="sm:col-span-2 space-y-1.5">
            <Label>Gallery photos (upload or paste links, one per line)</Label>
            <Textarea data-testid="admin-modal-gallery-input" rows={3} value={form.galleryText} onChange={set("galleryText")} placeholder={"https://...\nhttps://..."} />
            <div className="flex items-center gap-2">
              <Button
                type="button"
                variant="outline"
                size="sm"
                data-testid="admin-modal-gallery-upload-btn"
                disabled={galleryUploading}
                onClick={() => galleryInputRef.current?.click()}
                className="rounded-full"
              >
                {galleryUploading ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : <Upload className="h-4 w-4 mr-1.5" />}
                {galleryUploading ? "Uploading..." : "Upload photos"}
              </Button>
              <input
                ref={galleryInputRef}
                type="file"
                accept="image/jpeg,image/png,image/webp,image/gif"
                multiple
                className="hidden"
                onChange={uploadGalleryPhotos}
                data-testid="admin-modal-gallery-file-input"
              />
            </div>
          </div>
          <div className="sm:col-span-2 space-y-1.5">
            <Label>House floor plans (optional — add as many as you like)</Label>
            {form.floorplans.map((url, j) => (
              <div key={j} className="flex gap-2 items-start">
                <div className="flex-1">
                  <ImageUpload
                    compact
                    testId={`admin-modal-floorplan-${j}`}
                    value={url}
                    onChange={(v) =>
                      setForm((f) => ({ ...f, floorplans: f.floorplans.map((u, k) => (k === j ? v : u)) }))
                    }
                  />
                </div>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  data-testid={`admin-modal-floorplan-remove-${j}`}
                  onClick={() => setForm((f) => ({ ...f, floorplans: f.floorplans.filter((_, k) => k !== j) }))}
                >
                  Remove
                </Button>
              </div>
            ))}
            <div className="flex items-center gap-2 flex-wrap">
              <Button
                type="button"
                variant="outline"
                size="sm"
                data-testid="admin-modal-floorplans-upload-btn"
                disabled={floorplansUploading}
                onClick={() => floorplanInputRef.current?.click()}
                className="rounded-full"
              >
                {floorplansUploading ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : <Upload className="h-4 w-4 mr-1.5" />}
                {floorplansUploading ? "Uploading..." : "Upload floor plans (select many)"}
              </Button>
              <Button
                type="button"
                variant="outline"
                size="sm"
                data-testid="admin-modal-floorplan-add"
                onClick={() => setForm((f) => ({ ...f, floorplans: [...f.floorplans, ""] }))}
                className="rounded-full"
              >
                + Add link
              </Button>
              <input
                ref={floorplanInputRef}
                type="file"
                accept="image/jpeg,image/png,image/webp,image/gif"
                multiple
                className="hidden"
                onChange={uploadFloorplans}
                data-testid="admin-modal-floorplans-file-input"
              />
            </div>
          </div>
          <div className="sm:col-span-2 space-y-1.5">
            <Label>Description</Label>
            <Textarea data-testid="admin-modal-description-input" rows={4} value={form.description} onChange={set("description")} placeholder="What makes this house special..." />
          </div>
          <div className="sm:col-span-2 space-y-1.5">
            <Label>Amenities (comma separated)</Label>
            <Input data-testid="admin-modal-amenities-input" value={form.amenitiesText} onChange={set("amenitiesText")} placeholder="All Bills Included, Weekly Cleaner, Furnished" />
          </div>

          <div className="sm:col-span-2 space-y-3 rounded-xl border border-[#E4DBD0] dark:border-[#352F2B] p-4">
            <div className="flex items-center justify-between">
              <Label className="text-base">Rooms in this property (HMO)</Label>
              <Button type="button" variant="outline" size="sm" data-testid="admin-modal-add-room-btn" onClick={addRoom} className="rounded-full">
                + Add room
              </Button>
            </div>
            {form.rooms.length === 0 && (
              <p className="text-xs text-[#8A837C] dark:text-[#8E867E]">
                No rooms added — the property will show as a single whole-home listing.
              </p>
            )}
            {form.rooms.map((room, i) => (
              <div key={i} data-testid={`admin-modal-room-${i}`} className="rounded-xl border border-[#EDE6DE] dark:border-[#28231F] p-3 space-y-2">
                <div className="flex gap-2">
                  <Input
                    data-testid={`admin-modal-room-title-${i}`}
                    value={room.title}
                    onChange={(e) => setRoom(i, "title", e.target.value)}
                    placeholder={`Room ${i + 1} name`}
                    className="flex-1"
                  />
                  <Button type="button" variant="outline" size="sm" data-testid={`admin-modal-room-remove-${i}`} onClick={() => removeRoom(i)}>
                    Remove
                  </Button>
                </div>
                <div className="flex gap-2">
                  <Input
                    data-testid={`admin-modal-room-rent-${i}`}
                    type="number"
                    min="0"
                    value={room.rent}
                    onChange={(e) => setRoom(i, "rent", e.target.value)}
                    placeholder="£/mo"
                    className="w-28"
                  />
                  <Input
                    data-testid={`admin-modal-room-size-${i}`}
                    type="number"
                    min="0"
                    step="0.01"
                    value={room.size_m2 ?? ""}
                    onChange={(e) => setRoom(i, "size_m2", e.target.value)}
                    placeholder="Size m²"
                    className="w-28"
                  />
                </div>
                <div className="flex gap-3 items-center flex-wrap">
                  <Select value={room.status} onValueChange={(v) => setRoom(i, "status", v)}>
                    <SelectTrigger data-testid={`admin-modal-room-status-${i}`} className="w-[160px]">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                    </SelectContent>
                  </Select>
                  <label className="inline-flex items-center gap-2 text-sm text-[#57524E] dark:text-[#C7BEB5]">
                    <input
                      type="checkbox"
                      data-testid={`admin-modal-room-ensuite-${i}`}
                      checked={!!room.ensuite}
                      onChange={(e) => setRoom(i, "ensuite", e.target.checked)}
                      className="h-4 w-4 accent-[#A05A32]"
                    />
                    En-suite
                  </label>
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs text-[#8A837C] dark:text-[#8E867E]">Room photos</Label>
                  <ImageUpload
                    compact
                    allowVideo
                    testId={`admin-modal-room-image-${i}`}
                    value={room.image || ""}
                    onChange={(v) => setRoom(i, "image", v)}
                  />
                  {(room.gallery || []).map((url, j) => (
                    <div key={j} className="flex gap-2 items-start">
                      <div className="flex-1">
                        <ImageUpload
                          compact
                          allowVideo
                          testId={`admin-modal-room-photo-${i}-${j}`}
                          value={url}
                          onChange={(v) =>
                            setRoom(i, "gallery", (room.gallery || []).map((u, k) => (k === j ? v : u)))
                          }
                        />
                      </div>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        data-testid={`admin-modal-room-photo-remove-${i}-${j}`}
                        onClick={() => setRoom(i, "gallery", (room.gallery || []).filter((_, k) => k !== j))}
                      >
                        Remove
                      </Button>
                    </div>
                  ))}
                  <div className="flex items-center gap-2 flex-wrap">
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      data-testid={`admin-modal-room-photos-upload-${i}`}
                      disabled={!!roomUploading[i]}
                      onClick={() => roomFileRefs.current[i]?.click()}
                      className="rounded-full"
                    >
                      {roomUploading[i] ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : <Upload className="h-4 w-4 mr-1.5" />}
                      {roomUploading[i] ? "Uploading..." : "Upload photos / videos (select many)"}
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      data-testid={`admin-modal-room-photo-add-${i}`}
                      onClick={() => setRoom(i, "gallery", [...(room.gallery || []), ""])}
                      className="rounded-full"
                    >
                      + Add photo link
                    </Button>
                    <input
                      ref={(el) => (roomFileRefs.current[i] = el)}
                      type="file"
                      accept="image/jpeg,image/png,image/webp,image/gif,video/mp4,video/webm,video/quicktime"
                      multiple
                      className="hidden"
                      onChange={(e) => uploadRoomPhotos(i, e)}
                      data-testid={`admin-modal-room-photos-file-${i}`}
                    />
                  </div>
                </div>
                <Input
                  data-testid={`admin-modal-room-description-${i}`}
                  value={room.description || ""}
                  onChange={(e) => setRoom(i, "description", e.target.value)}
                  placeholder="Short room description (optional)"
                />
              </div>
            ))}
          </div>

          <div className="sm:col-span-2 flex justify-end gap-3 pt-2">
            <Button type="button" variant="outline" data-testid="admin-modal-cancel-btn" onClick={() => onOpenChange(false)} className="rounded-full">
              Cancel
            </Button>
            <Button type="submit" data-testid="admin-modal-save-btn" disabled={saving} className="rounded-full bg-[#A05A32] hover:bg-[#864522] text-[#FAF8F5]">
              {saving ? "Saving..." : property ? "Save changes" : "Publish property"}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
